import React, { useState } from 'react';
import { useSidebar } from '../../contexts/SidebarContext';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import { buildApiUrl } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/ui/Button';
import ExpenseModal from './components/ExpenseModal';
import ExpenseTable from './components/ExpenseTable';
import CategorySummaryCards from './components/CategorySummaryCards';
import ExpenseProjectionChart from './components/ExpenseProjectionChart';
import LifestyleEstimationSidebar from './components/LifestyleEstimationSidebar';
import DrawdownPrioritySection from './components/DrawdownPrioritySection';
import { useExpenses } from '../../hooks/useExpenses';
import { useUserData } from '../../hooks/useUserData';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useTour } from '../../contexts/TourContext';
import { useDemo } from '../../contexts/DemoContext';
import VideoModal from '../../components/ui/VideoModal';
import { getVideoForPage } from '../../config/videoConfig';
import DemoModeBanner from '../../components/demo/DemoModeBanner';

const ExpenseTracking = () => {
  const { sidebarExpanded, toggleSidebar } = useSidebar();
  const [activeTab, setActiveTab] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [showLifestyleSidebar, setShowLifestyleSidebar] = useState(false);
  const [isVideoOpen, setIsVideoOpen] = useState(false);
  
  // Use the expenses hook for API integration
  const { 
    expenses, 
    loading, 
    error, 
    createExpense, 
    updateExpense, 
    deleteExpense 
  } = useExpenses();

  // Access setup projection horizon
  const { userData, loading: userDataLoading } = useUserData();
  const { getAuthHeaders } = useAuth();
  const { canWrite, canProjectBeyond5Years } = useSubscription();
  const { startExpenseTrackingTour, autoStartTour } = useTour();
  const { getDemoQueryParams, isDemoModeActive } = useDemo();
  const effectiveCanWrite = canWrite && !isDemoModeActive;
  const videoConfig = getVideoForPage('expense-tracking');
  const [quickStats, setQuickStats] = useState(null);
  
  React.useEffect(() => {
    const fetchQuickStats = async () => {
      try {
        const queryParams = getDemoQueryParams();
        const res = await fetch(buildApiUrl(`/api/expense/quick-stats${queryParams}`), { headers: getAuthHeaders() });
        if (!res.ok) throw new Error('Failed to load quick stats');
        const json = await res.json();
        if (json?.success) setQuickStats(json.data);
      } catch (e) {
        console.error(e);
        // Keep UI fallback values
      }
    };
    fetchQuickStats();
  }, [expenses, getDemoQueryParams, getAuthHeaders]);
  const projectionHorizon = userData?.setup?.projectionHorizon || 20;

  // Auto-start tour for first-time visitors
  React.useEffect(() => {
    if (!userDataLoading && userData) {
      autoStartTour('expense-tracking', startExpenseTrackingTour);
    }
  }, [userDataLoading, userData, autoStartTour, startExpenseTrackingTour]);

  const handleSaveExpense = async (expenseData) => {
    try {
      if (editingExpense) {
        // Update existing expense
        await updateExpense(expenseData.id, expenseData);
        setIsModalOpen(false);
        setEditingExpense(null);
      } else {
        // Add new expense
        await createExpense(expenseData);
        setIsModalOpen(false);
      }
    } catch (error) {
      console.error('Error saving expense:', error);
    }
  };

  const handleEditExpense = (expense) => {
    setEditingExpense(expense);
    setIsModalOpen(true);
  };

  const handleDeleteExpense = async (expenseId) => {
    try {
      await deleteExpense(expenseId);
    } catch (error) {
      console.error('Error deleting expense:', error);
    }
  };

  const handleAddTemplate = (templateExpense) => {
    setEditingExpense(null);
    setIsModalOpen(true);
    // The template data will be handled by the modal
  };

  const handleBulkImport = () => {
    // TODO: Implement bulk import functionality
    console.log('Bulk import functionality to be implemented');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingExpense(null);
  };

  // Filter expenses based on active tab
  const filteredExpenses = activeTab === 'all' 
    ? expenses 
    : expenses.filter(expense => expense.category === activeTab);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading expense data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Icon name="AlertCircle" size={48} className="text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-foreground mb-2">Error Loading Data</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const categories = [
    { key: 'all', label: 'All', icon: 'List' },
    { key: 'medical', label: 'Medical & Healthcare', icon: 'Heart' },
    { key: 'travel', label: 'Travel & Leisure', icon: 'Plane' },
    { key: 'personal', label: 'Personal & Lifestyle', icon: 'User' },
    { key: 'home', label: 'Home & Utilities', icon: 'Home' },
    { key: 'food', label: 'Food & Dining', icon: 'Utensils' },
    { key: 'transport', label: 'Transportation', icon: 'Car' },
    { key: 'kids', label: 'Children & Education', icon: 'Baby' },
    { key: 'parents', label: 'Parents & Elderly Care', icon: 'Users' }
  ];

  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      <Sidebar />
      
      <Header 
        sidebarExpanded={sidebarExpanded}
        onSidebarToggle={toggleSidebar}
      />

      <DemoModeBanner />

      <main 
        className={`
          transition-all duration-300 ease-spring pt-16 pb-20 lg:pb-8
          ${sidebarExpanded ? 'lg:ml-64' : 'lg:ml-16'}
          ${showLifestyleSidebar ? 'lg:mr-80' : ''}
        `}
      >
        <div className="p-4 sm:p-6 py-6 sm:py-8">
          <Breadcrumb />
          
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 sm:mb-8" data-tour="expense-header">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-foreground mb-2">Expense Tracking</h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                {`Categorize and project expenses across ${projectionHorizon} years with inflation adjustments`}
              </p>
            </div>
            
            <div className="flex items-center gap-2 sm:gap-3">
              {false && (
                <Button
                  variant="outline"
                  onClick={handleBulkImport}
                  iconName="Upload"
                  iconPosition="left"
                  className="text-xs sm:text-sm"
                >
                  <span className="hidden sm:inline">Import CSV</span>
                  <span className="sm:hidden">Import</span>
                </Button>
              )}

              {false && (
                <Button
                  variant="outline"
                  onClick={() => setShowLifestyleSidebar(!showLifestyleSidebar)}
                  iconName="Calculator"
                  iconPosition="left"
                  className="text-xs sm:text-sm"
                >
                  <span className="hidden sm:inline">Lifestyle Tools</span>
                  <span className="sm:hidden">Tools</span>
                </Button>
              )}

              <Button
                variant="outline"
                onClick={() => setIsVideoOpen(true)}
                iconName="Video"
                iconPosition="left"
                className="hidden sm:inline-flex text-xs sm:text-sm"
              >
                Learn
              </Button>

              <Button
                variant="outline"
                onClick={startExpenseTrackingTour}
                iconName="HelpCircle"
                iconPosition="left"
                className="hidden sm:inline-flex text-xs sm:text-sm"
              >
                Tour
              </Button>

              <Button
                onClick={() => setIsModalOpen(true)}
                iconName="Plus"
                iconPosition="left"
                className="text-xs sm:text-sm disabled:opacity-60"
                    disabled={!effectiveCanWrite}
              >
                <span className="hidden sm:inline">Add Expense</span>
                <span className="sm:hidden">Add</span>
              </Button>
            </div>
          </div>

          <div data-tour="expense-summary">
            <CategorySummaryCards expenses={expenses} />
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6 lg:gap-8 mb-6 sm:mb-8">
            <div className="xl:col-span-2" data-tour="expense-chart">
              <ExpenseProjectionChart expenses={expenses} projectionHorizon={projectionHorizon} />
            </div>
            
          <div className="bg-card border border-border rounded-lg p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4">Quick Stats</h3>
              
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="Receipt" size={20} className="text-accent" />
                    <span className="text-sm font-medium text-foreground">Total Expenses</span>
                  </div>
                <span className="text-lg font-semibold text-foreground">{quickStats?.count ?? expenses.length}</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="TrendingUp" size={20} className="text-warning" />
                    <span className="text-sm font-medium text-foreground">Avg Inflation</span>
                  </div>
                <span className="text-lg font-semibold text-foreground">{((quickStats?.avgInflation ?? (expenses.reduce((sum, e) => sum + e.inflationRate, 0) / (expenses.length || 1))) || 0).toFixed(1)}%</span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="Calendar" size={20} className="text-primary" />
                    <span className="text-sm font-medium text-foreground">Planning Horizon</span>
                  </div>
                <span className="text-lg font-semibold text-foreground">
                  {canProjectBeyond5Years ? (quickStats?.planningHorizon ?? projectionHorizon) : 5} Years
                </span>
                </div>
                
                <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                  <div className="flex items-center gap-3">
                    <Icon name="Target" size={20} className="text-success" />
                    <span className="text-sm font-medium text-foreground">Avg Probability</span>
                  </div>
                <span className="text-lg font-semibold text-foreground">{((quickStats?.avgProbability ?? (expenses.reduce((sum, e) => sum + e.probability, 0) / (expenses.length || 1))) || 0).toFixed(0)}%</span>
                </div>
              </div>
            </div>
          </div>

          

          <div className="bg-card border border-border rounded-lg">
            <div className="border-b border-border" data-tour="expense-categories">
              <div className="flex flex-wrap items-center gap-2 p-2">
                {categories.map((category) => (
                  <button
                    key={category.key}
                    onClick={() => setActiveTab(category.key)}
                    className={`
                      flex items-center gap-2 px-3 py-2 text-xs sm:text-sm font-medium whitespace-normal sm:whitespace-nowrap
                      border-b-2 transition-colors duration-200
                      ${activeTab === category.key
                        ? 'border-primary text-primary bg-primary/5' :'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }
                    `}
                  >
                    <Icon name={category.icon} size={16} />
                    {category.label}
                    <span className="ml-2 px-2 py-1 text-xs bg-muted rounded-full">
                      {category.key === 'all' 
                        ? expenses.length 
                        : expenses.filter(e => e.category === category.key).length}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="p-6" data-tour="expense-table">
              <ExpenseTable
                expenses={filteredExpenses}
                category={activeTab}
                onEdit={handleEditExpense}
                onDelete={handleDeleteExpense}
              />
            </div>
          </div>

          <div className="mt-6" data-tour="expense-drawdown">
            <DrawdownPrioritySection />
          </div>
        </div>
      </main>

      {showLifestyleSidebar && (
        <div className="fixed right-0 top-16 bottom-0 z-1000">
          <LifestyleEstimationSidebar onAddTemplate={handleAddTemplate} />
        </div>
      )}

      <ExpenseModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveExpense}
        editingExpense={editingExpense}
      />

      {/* Video Modal */}
      <VideoModal
        isOpen={isVideoOpen}
        onClose={() => setIsVideoOpen(false)}
        videoUrl={videoConfig?.url}
        title={videoConfig?.title}
      />
    </div>
  );
};

export default ExpenseTracking;